import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import {
  DepartmentType,
  EntityType,
  EventType,
  GqlService,
  RoleType,
  UserPermissionInput,
  UserTrialInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';

import { TrialUserStore } from './trial-user.store';

const mapPermissionArrToObj = (
  arr: {
    permission_id: string;
    permission_roles: string;
  }[]
) => {
  const obj: { [k: string]: 'E' | 'U' } = {};

  arr.forEach((x) => {
    obj[x.permission_id] = x.permission_roles as 'E' | 'U';
  });

  return obj;
};

@Injectable({ providedIn: 'root' })
export class TrialUserService {
  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private trialUserStore = inject(TrialUserStore);

  async add({
    department,
    email,
    family_name,
    given_name,
    role,
    title,
    trial_id,
  }: {
    department: DepartmentType[];
    email: string;
    family_name: string;
    given_name: string;
    role: RoleType;
    title: string;
    trial_id: string;
  }) {
    this.trialUserStore.setLoading(true);

    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createUser$({
        email,
        family_name,
        given_name,
        title,
      })
    );

    if (success && data) {
      const {
        data: data2,
        errors: trialErrors,
        success: trialSuccess,
      } = await this.addToTrial({
        department,
        family_name,
        given_name,
        role,
        title,
        trial_id,
        user_email: email,
        user_id: data.sub,
      });

      if (trialSuccess && data2) {
        this.trialUserStore.add({
          __typename: 'User',
          department: data2.department,
          email,
          family_name,
          given_name,
          last_login_date: data2.last_login_date,
          organization: data2.organization,
          permissions: mapPermissionArrToObj(
            data2.permissions as {
              permission_id: string;
              permission_roles: 'E' | 'U';
            }[]
          ),
          role: data2.role,
          sub: data.sub,
          title,
        });
        this.trialUserStore.setLoading(false);

        return true;
      }

      alert(trialErrors);
    } else {
      alert(errors);
    }

    this.trialUserStore.setLoading(false);

    return false;
  }

  addToTrial({
    department,
    family_name,
    given_name,
    role,
    title,
    trial_id,
    user_email,
    user_id,
  }: UserTrialInput) {
    return firstValueFrom(
      this.gqlService.addUserToTrial$({
        department,
        family_name,
        given_name,
        role,
        title,
        trial_id,
        user_email,
        user_id,
      })
    );
  }

  async batchProcessPermissionUpdates(
    values: {
      permission: string;
      sub: string;
      value: boolean;
    }[]
  ): Promise<void> {
    this.trialUserStore.setLoading(true);

    const chunkArray = (
      inputArray: {
        permission: string;
        sub: string;
        value: boolean;
      }[],
      chunkSize = 100
    ): {
      permission: string;
      sub: string;
      value: boolean;
    }[][] => {
      const result = [];

      for (let i = 0; i < inputArray.length; i += chunkSize) {
        result.push(inputArray.slice(i, i + chunkSize));
      }

      return result;
    };

    await Promise.all(
      chunkArray(values).map(
        (
          batch: {
            permission: string;
            sub: string;
            value: boolean;
          }[]
        ) => this.updatePermissions(batch)
      )
    );

    this.eventService
      .processEvent$({
        entity_id: this.mainQuery.getValue().trialKey,
        entity_type: EntityType.PERMISSION,
        type: EventType.REFRESH_USER_PERMISSIONS,
      })
      .subscribe(() => {
        values.forEach((v) => {
          this.trialUserStore.update(v.sub, ({ permissions }) => {
            return {
              permissions: { ...permissions, [v.permission]: v.value ? 'E' : 'U' },
            };
          });
        });

        this.trialUserStore.setLoading(false);
      });
  }

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.trialUserStore.setLoading(true);
        this.trialUserStore.remove(() => true);

        return this.gqlService.listUsers$(false).pipe(
          tap((x) => {
            if (x.data) {
              const newData = x.data.map((y) => {
                const arr = y.permissions;
                const permissions = mapPermissionArrToObj(arr);

                return {
                  ...y,
                  permissions,
                };
              });

              this.trialUserStore.set(newData);
            }

            this.trialUserStore.setLoading(false);
          })
        );
      })
    );
  }

  async reinvite(email: string) {
    this.trialUserStore.setLoading(true);

    const { data, errors, success } = await firstValueFrom(
      this.gqlService.resendUserInvite$({ email })
    );

    if (success && data) {
      this.overlayService.success('User successfully reinvited');
    } else {
      this.overlayService.error(errors);
    }

    this.trialUserStore.setLoading(false);

    return false;
  }

  async remove(user_id: string) {
    const { errors, success } = await firstValueFrom(this.gqlService.removeUserFromTrial$(user_id));

    if (success) {
      this.trialUserStore.remove(user_id);
      this.overlayService.success('User successfully removed');
    } else {
      this.overlayService.error(['User could not be removed', ...errors]);
    }

    return { errors, success };
  }

  async update({
    department,
    email,
    family_name,
    given_name,
    role,
    sub,
    title,
  }: Omit<UserTrialInput, 'trial_id' | 'user_id'> & { email: string; sub: string }) {
    const { data, errors, success } = await this.addToTrial({
      department,
      family_name,
      given_name,
      role,
      title,
      trial_id: this.mainQuery.getValue().trialKey,
      user_id: sub as string,
    });

    if (success && data) {
      this.trialUserStore.update(sub, {
        department: data.department,
        email,
        family_name,
        given_name,
        organization: data.organization,
        role,
        sub,
        title,
      });
    } else {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }

  async updatePermissions(
    values: {
      permission: string;
      sub: string;
      value: boolean;
    }[]
  ): Promise<boolean> {
    const trial_id = this.mainQuery.getValue().trialKey;
    const input = values.map(({ permission, sub, value }) => {
      return <UserPermissionInput>{
        permission_id: permission,
        permission_roles: value ? 'E' : 'U',
        trial_id,
        user_id: sub,
      };
    });

    const { data, errors, success } = await firstValueFrom(
      this.gqlService.batchUpdateUserPermissions$(input)
    );

    if (success && data) {
      return true;
    } else {
      console.error(errors);

      return false;
    }
  }
}
